'use client'

import { create } from 'zustand'
import { createContext, useContext, useSyncExternalStore } from 'react'
import { z } from 'zod'
import { isEqual } from 'lodash-es'
import {
  mergeRoomHistory,
  prependRoomHistory,
} from '@/features/veluvit/lib/room-history'

import type {
  Bootstrap,
  Room,
  RoomMessage,
  RoomResult,
  User,
  Wallet,
} from '@/features/veluvit/types/api.types'

/**
 * 세션 캐시 — 레퍼런스 v2-live.js 의 전역 B(user·rooms·wallet·ownedCards…)를 옮긴 것.
 * 이벤트 핸들러가 방·지갑을 동기로 읽고, 요청 응답을 즉시 병합해야 해서 SWR 대신 스토어로 둔다.
 */
type SettingsOverride = Pick<User['settings'], 'showAdult'>

type SessionState = {
  ready: boolean
  error: string | null
  data: Bootstrap | null
  /** 전체 메시지를 받아 둔 방 (목록의 요약본은 메시지 2개뿐) */
  rooms: Record<string, Room>
  /** 작품별로 이어서 열 방 id — 레퍼런스 localStorage 'rooms' */
  selected: Record<string, string>
  /** "다른 시작으로 새 대화" 를 고른 작품 */
  fresh: Record<string, boolean>
  /** 계정이 바뀔 때마다 오른다 — 늦게 도착한 이전 계정 응답을 버린다 */
  identity: number
  settingsOverride: SettingsOverride | null
  settingsRevision: number
  updateSettings: (patch: SettingsOverride) => void
  syncSettings: (settings: User['settings'], revision: number) => void
  setError: (message: string | null) => void
  applyBootstrap: (data: Bootstrap) => void
  applyWallet: (wallet: Wallet) => void
  applyRoom: (room: Room) => void
  applyResult: (result: RoomResult) => void
  prependHistory: (
    roomId: string,
    beforeId: string,
    messages: RoomMessage[],
    page: NonNullable<Room['messagePage']>
  ) => boolean
  revokeRestrictedRooms: (roomId?: string) => void
  select: (workId: string, roomId: string | null) => void
  markFresh: (workId: string, value: boolean) => void
}

const SELECTED_KEY = 'veluvit:rooms'

export function mergeSettings(
  current: User['settings'] = {},
  patch: User['settings'] = {}
): User['settings'] {
  return {
    ...current,
    ...patch,
    ...(patch.notifications
      ? { notifications: { ...current.notifications, ...patch.notifications } }
      : {}),
  }
}

function readSelected(userId: string | undefined): Record<string, string> {
  try {
    const raw = localStorage.getItem(`${SELECTED_KEY}:${userId || 'visitor'}`)
    const parsed: unknown = raw ? JSON.parse(raw) : {}
    const result = z.record(z.string(), z.string()).safeParse(parsed)
    return result.success ? result.data : {}
  } catch {
    return {}
  }
}

function writeSelected(
  userId: string | undefined,
  value: Record<string, string>
) {
  try {
    localStorage.setItem(
      `${SELECTED_KEY}:${userId || 'visitor'}`,
      JSON.stringify(value)
    )
  } catch {
    // 저장소를 쓸 수 없는 환경(사생활 보호 모드)에서는 이어보기만 포기한다
  }
}

function roomChanges(state: SessionState, room: Room) {
  room = mergeRoomHistory(state.rooms[room.id], room)
  const rooms =
    state.rooms[room.id] === room ? null : { ...state.rooms, [room.id]: room }
  const selected = state.selected[room.workId]
    ? null
    : { ...state.selected, [room.workId]: room.id }
  const sorted = state.data
    ? [room, ...state.data.rooms.filter((item) => item.id !== room.id)].sort(
        (a, b) => b.updatedAt.localeCompare(a.updatedAt)
      )
    : null
  const roomList =
    sorted &&
    (sorted.length !== state.data?.rooms.length ||
      sorted.some((item, index) => item !== state.data?.rooms[index]))
      ? sorted
      : null
  return { rooms, selected, roomList }
}

const clientSessionStore = create<SessionState>()((set, get) => ({
  ready: false,
  error: null,
  data: null,
  rooms: {},
  selected: {},
  fresh: {},
  identity: 0,
  settingsOverride: null,
  settingsRevision: 0,
  updateSettings: (patch) => {
    const state = get()
    if (!state.data?.user) return
    const settingsOverride = mergeSettings(state.settingsOverride ?? {}, patch)
    set({
      settingsOverride,
      settingsRevision: state.settingsRevision + 1,
      data: {
        ...state.data,
        user: {
          ...state.data.user,
          settings: mergeSettings(state.data.user.settings, settingsOverride),
        },
      },
    })
  },
  syncSettings: (settings, revision) => {
    const state = get()
    const previous = state.settingsOverride
    if (!state.data?.user || !previous || state.settingsRevision !== revision)
      return
    const settingsOverride: SettingsOverride = {
      ...(previous.showAdult !== undefined
        ? { showAdult: settings.showAdult ?? false }
        : {}),
    }
    set({
      settingsOverride,
      data: {
        ...state.data,
        user: {
          ...state.data.user,
          settings: mergeSettings(state.data.user.settings, settingsOverride),
        },
      },
    })
  },
  setError: (message) => set({ error: message }),
  applyBootstrap: (incoming) => {
    const previous = get().data
    const changed = previous?.user?.id !== incoming.user?.id
    const settingsOverride = changed ? null : get().settingsOverride
    // 실제 값이 같을 때만 공개 목록과 카탈로그에 쓰는 모드 배열의 참조를 유지한다.
    const works =
      !changed &&
      previous?.works &&
      incoming.works &&
      isEqual(previous.works, incoming.works)
        ? previous.works
        : incoming.works
    const modes =
      !changed &&
      previous &&
      isEqual(previous.config.modes, incoming.config.modes)
        ? previous.config.modes
        : incoming.config.modes
    const user =
      incoming.user && settingsOverride
        ? {
            ...incoming.user,
            settings: mergeSettings(incoming.user.settings, settingsOverride),
          }
        : incoming.user
    const data = {
      ...incoming,
      user,
      works,
      config: { ...incoming.config, modes },
    }
    const cachedRooms = { ...get().rooms }
    // 같은 계정의 인증 만료도 이전 상세 projection을 지워야 한다.
    for (const summary of data.rooms) {
      if (summary.locked) cachedRooms[summary.id] = summary
    }
    if (data.config.adult?.eligible === false) {
      for (const [id, room] of Object.entries(cachedRooms)) {
        if (
          !room.locked &&
          (room.requiresAdult || room.adultHistory || room.preset === 'adult')
        )
          delete cachedRooms[id]
      }
    }
    const selected = changed
      ? readSelected(data.user?.id)
      : { ...get().selected }
    for (const room of data.rooms) {
      if (!room.archived && !selected[room.workId])
        selected[room.workId] = room.id
    }
    writeSelected(data.user?.id, selected)
    set({
      data,
      ready: true,
      error: null,
      selected,
      rooms: changed ? {} : cachedRooms,
      ...(changed
        ? {
            fresh: {},
            identity: get().identity + 1,
            settingsOverride: null,
            settingsRevision: get().settingsRevision + 1,
          }
        : {}),
    })
  },
  applyWallet: (wallet) => {
    const data = get().data
    if (data && !isEqual(data.wallet, wallet))
      set({ data: { ...data, wallet } })
  },
  applyRoom: (room) => {
    const state = get()
    const { rooms, selected, roomList } = roomChanges(state, room)
    if (!rooms && !selected && !roomList) return
    if (selected) writeSelected(state.data?.user?.id, selected)
    set({
      ...(rooms ? { rooms } : {}),
      ...(selected ? { selected } : {}),
      ...(roomList && state.data
        ? { data: { ...state.data, rooms: roomList } }
        : {}),
    })
  },
  applyResult: (result) => {
    const state = get()
    const { rooms, selected, roomList } = result.room
      ? roomChanges(state, result.room)
      : { rooms: null, selected: null, roomList: null }
    let data = state.data
    if (data && roomList) data = { ...data, rooms: roomList }
    if (data && result.wallet && !isEqual(data.wallet, result.wallet))
      data = { ...data, wallet: result.wallet }
    if (data && result.unlocked?.length) {
      let owned = data.ownedCards
      for (const card of result.unlocked) {
        const cardId = card.cardId || card.id
        if (
          !owned.some(
            (x) => x.workId === card.workId && (x.cardId || x.id) === cardId
          )
        ) {
          if (owned === data.ownedCards) owned = data.ownedCards.slice()
          owned.push(card)
        }
      }
      if (owned !== data.ownedCards) data = { ...data, ownedCards: owned }
    }
    if (!rooms && !selected && data === state.data) return
    if (selected) writeSelected(data?.user?.id, selected)
    set({
      ...(rooms ? { rooms } : {}),
      ...(selected ? { selected } : {}),
      ...(data !== state.data ? { data } : {}),
    })
  },
  prependHistory: (roomId, beforeId, messages, page) => {
    const state = get()
    const room = state.rooms[roomId]
    if (
      !room?.messagePage ||
      room.messagePage.nextBeforeId !== beforeId ||
      room.messagePage.start !== page.end
    )
      return false
    const next = prependRoomHistory(room, messages, page)
    set({
      rooms: { ...state.rooms, [roomId]: next },
      data: state.data
        ? {
            ...state.data,
            rooms: state.data.rooms.map((row) =>
              row.id === roomId ? next : row
            ),
          }
        : null,
    })
    return true
  },
  revokeRestrictedRooms: (roomId) => {
    const state = get()
    const restricted = (room: Room) =>
      room.id === roomId ||
      room.requiresAdult ||
      room.adultHistory ||
      room.preset === 'adult'
    const removed = new Set(
      Object.values(state.rooms)
        .filter(restricted)
        .map((room) => room.id)
    )
    for (const room of state.data?.rooms || [])
      if (restricted(room)) removed.add(room.id)
    if (roomId) removed.add(roomId)
    const selected = Object.fromEntries(
      Object.entries(state.selected).filter(([, id]) => !removed.has(id))
    )
    writeSelected(state.data?.user?.id, selected)
    set({
      rooms: Object.fromEntries(
        Object.entries(state.rooms).filter(([id]) => !removed.has(id))
      ),
      selected,
      data: state.data
        ? {
            ...state.data,
            rooms: state.data.rooms.filter((room) => !removed.has(room.id)),
          }
        : null,
    })
  },
  select: (workId, roomId) => {
    const selected = { ...get().selected }
    if (roomId) selected[workId] = roomId
    else delete selected[workId]
    writeSelected(get().data?.user?.id, selected)
    set({ selected })
  },
  markFresh: (workId, value) =>
    set({ fresh: { ...get().fresh, [workId]: value } }),
}))

export const SessionSnapshotContext = createContext<SessionState | null>(null)

function useSessionSnapshot(): SessionState
function useSessionSnapshot<T>(selector: (state: SessionState) => T): T
function useSessionSnapshot<T = SessionState>(
  selector?: (state: SessionState) => T
): T | SessionState {
  const snapshot = useContext(SessionSnapshotContext)
  const select = selector ?? ((state: SessionState) => state)
  return useSyncExternalStore(
    clientSessionStore.subscribe,
    () => select(clientSessionStore.getState()),
    () => select(snapshot ?? clientSessionStore.getInitialState())
  )
}

export const useSessionStore = Object.assign(useSessionSnapshot, {
  getState: clientSessionStore.getState,
  getInitialState: clientSessionStore.getInitialState,
  setState: clientSessionStore.setState,
  subscribe: clientSessionStore.subscribe,
})

/** 작품의 현재 방 — 선택된 방, 없으면 보관하지 않은 최근 방 */
export function roomIn(
  state: Pick<SessionState, 'data' | 'rooms' | 'selected'>,
  workId: string
): Room | undefined {
  const { data, rooms, selected } = state
  const id = selected[workId]
  if (id && rooms[id]?.workId === workId) return rooms[id]
  const summary = id
    ? data?.rooms.find((r) => r.id === id && r.workId === workId)
    : data?.rooms.find((r) => r.workId === workId && !r.archived)
  return summary ? rooms[summary.id] || summary : undefined
}

/** 지금 상태에서 한 번 읽기 — 이벤트 핸들러 · 서비스용. 렌더 안에서는 useRoom 을 쓴다 */
export function roomFor(workId: string): Room | undefined {
  return roomIn(useSessionStore.getState(), workId)
}

/**
 * 렌더용 방 구독 — 값을 훅에서 받아야 React Compiler 가 의존성으로 본다.
 * `useSessionStore((s) => s.rooms)` 로 구독만 하고 `roomFor(workId)` 를 따로 부르면 컴파일러가 roomFor 결과를 workId 기준으로 메모해
 * 방이 바뀌어도(모델 변경 · 답장) 이전 방 객체를 계속 보여 줬다(2026-10-13 모델 고르기 라디오가 안 옮겨 가던 원인)
 */
export function useRoom(workId: string): Room | undefined {
  return useSessionStore((s) => roomIn(s, workId))
}
